import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type CampaignChannelDraft,
  type CampaignChannelResponse,
  type DeliverableItem,
  type DeliverableRequest,
} from "@/lib/api";
import { fmtDate, fmtNumber, fmtTimestamp } from "@/lib/format";
import { Badge, decisionVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Lbl } from "@/components/ui/Lbl";
import { Field } from "@/components/ui/Field";

// CampaignChannelCard + its sub-components (DeliverableCard, MilestoneTable,
// EditCampaignChannelModal, RecordDeliverableModal). v2.4.0 — extracted from
// CampaignDetail.tsx (was 1299 LOC) to keep the route file under ~600 LOC.
// These all share a single domain object — one campaign_channel and its
// deliverables — so they ship together.

export function CampaignChannelCard({
  cc,
  deliverables,
  deliverablesLoading,
  onRecordDeliverable,
  campaignID,
}: {
  cc: CampaignChannelResponse;
  deliverables: DeliverableItem[];
  deliverablesLoading: boolean;
  onRecordDeliverable: () => void;
  campaignID: number;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const delMut = useMutation({
    mutationFn: () => endpoints.deleteCampaignChannel(campaignID, cc.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign", campaignID] });
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
  });
  // v2.4.3 — per-KOL incremental refresh. Was only available as "refresh
  // every KOL's deliverables at once" on the page header; this lets the
  // operator pull fresh stats for just this one channel without burning
  // quota on the rest. Reuses /api/channels/refresh in incremental mode
  // (default), so this is the same cheap watermark-based pull as the
  // 频道库 "增量刷新" button.
  const refreshMut = useMutation({
    mutationFn: () => endpoints.refreshChannels([cc.channel_id]),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["campaign", campaignID] });
      qc.invalidateQueries({ queryKey: ["campaign-deliverables", campaignID] });
      qc.invalidateQueries({ queryKey: ["sponsorships"] });
      const r = data.results[0];
      const ok = r?.ok;
      const videos = r?.videos_upserted ?? 0;
      const stats = r?.stats_refreshed ?? 0;
      const detection = data.detection_sponsorships ?? 0;
      if (ok) {
        alert(`刷新成功:新增 ${videos} 个视频,刷新 ${stats} 个统计,新检出 ${detection} 个推广`);
      } else {
        alert(`刷新失败:${r?.error ?? "未知"}`);
      }
    },
    onError: (err) => alert((err as Error).message),
  });

  return (
    <article className="rounded-md border border-border bg-background p-4">
      <header className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-base font-medium">
            <Link
              to={`/channels/${cc.channel_id}`}
              className="text-primary hover:underline"
            >
              {cc.channel_name ?? cc.channel_handle ?? `#${cc.channel_id}`}
            </Link>
            {cc.channel_platform && (
              <span className="text-xs text-muted-foreground">
                {cc.channel_platform}
              </span>
            )}
            {cc.deal_amount != null && (
              <Badge variant="info">
                {cc.payment_currency === "USD" ? "$" : ""}
                {fmtNumber(cc.deal_amount)}
                {cc.payment_currency && cc.payment_currency !== "USD" ? ` ${cc.payment_currency}` : ""}
              </Badge>
            )}
            {cc.has_deal_amount && cc.deal_amount == null && (
              <Badge variant="secondary">金额解密失败</Badge>
            )}
          </div>
          {cc.channel_handle && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              {cc.channel_handle} ·{" "}
              {fmtNumber(cc.channel_follower_count ?? null) ?? "—"} 粉丝
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending}
            title="只刷新这一个 KOL 的视频 + 检测推广(增量,不再拉历史)"
          >
            {refreshMut.isPending ? "刷新中…" : "↻ 刷新"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            编辑
          </Button>
          <Button size="sm" variant="outline" onClick={onRecordDeliverable}>
            + 交付内容
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (
                confirm(
                  `确认从本投放移除 ${cc.channel_name ?? `#${cc.channel_id}`}？已交付内容不会被删除。`,
                )
              ) {
                delMut.mutate();
              }
            }}
            disabled={delMut.isPending}
          >
            移除
          </Button>
        </div>
      </header>

      {editing && (
        <EditCampaignChannelModal
          cc={cc}
          campaignID={campaignID}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}

      <dl className="mb-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm md:grid-cols-4">
        <Field label="返佣码">
          {cc.referral_code ? (
            <code className="rounded bg-muted px-1 text-xs">{cc.referral_code}</code>
          ) : (
            "—"
          )}
        </Field>
        <Field label="KOL UID">
          {cc.kol_uid ? (
            <code className="rounded bg-muted px-1 text-xs">{cc.kol_uid}</code>
          ) : (
            "—"
          )}
        </Field>
        <Field label="金额">
          {cc.deal_amount != null
            ? `${cc.payment_currency === "USD" ? "$" : ""}${fmtNumber(cc.deal_amount)}${cc.payment_currency && cc.payment_currency !== "USD" ? ` ${cc.payment_currency}` : ""}`
            : cc.has_deal_amount
              ? <span className="text-destructive">解密失败</span>
              : "—"}
        </Field>
        <Field label="约定交付">{cc.agreed_deliverables ?? "—"}</Field>
        {cc.notes && (
          <div className="col-span-2 md:col-span-4">
            <dt className="text-xs uppercase tracking-wide text-muted-foreground">
              备注
            </dt>
            <dd className="whitespace-pre-wrap text-sm">{cc.notes}</dd>
          </div>
        )}
      </dl>

      <DeliverableList items={deliverables} loading={deliverablesLoading} />
    </article>
  );
}

// v2.4.3 — tabs split deliverables by review_status. Before this they all
// piled into one list, so a campaign with many confirmed entries buried the
// 1-2 pending ones the operator actually needed to act on. Default tab is
// "pending" when any exist (calls the operator's attention), otherwise
// "confirmed" (the success case for boss-facing review).
function DeliverableList({ items, loading }: { items: DeliverableItem[]; loading: boolean }) {
  const counts = {
    pending: items.filter((d) => d.review_status === "pending").length,
    confirmed: items.filter((d) => d.review_status === "confirmed").length,
    rejected: items.filter((d) => d.review_status === "rejected").length,
  };
  const initial = counts.pending > 0 ? "pending" : counts.confirmed > 0 ? "confirmed" : "pending";
  const [tab, setTab] = useState<"pending" | "confirmed" | "rejected">(initial);
  const visible = items.filter((d) => d.review_status === tab);

  return (
    <div className="mt-2 border-t border-border/60 pt-3">
      <div className="mb-2 flex items-center gap-3 text-xs">
        <span className="font-semibold text-muted-foreground">已交付内容</span>
        <div className="flex gap-1">
          <TabButton active={tab === "pending"} onClick={() => setTab("pending")} count={counts.pending} variant="warning">待审</TabButton>
          <TabButton active={tab === "confirmed"} onClick={() => setTab("confirmed")} count={counts.confirmed} variant="success">已确认</TabButton>
          <TabButton active={tab === "rejected"} onClick={() => setTab("rejected")} count={counts.rejected} variant="muted">已拒绝</TabButton>
        </div>
      </div>
      {loading && <p className="text-xs text-muted-foreground">加载中…</p>}
      {!loading && items.length === 0 && (
        <p className="text-xs text-muted-foreground">
          还没有交付内容。等 sponsorship_detection 自动匹配返佣码，或者点"+ 交付内容"手动加。
        </p>
      )}
      {!loading && items.length > 0 && visible.length === 0 && (
        <p className="text-xs text-muted-foreground">本状态下无记录</p>
      )}
      {visible.length > 0 && (
        <ul className="space-y-3">
          {visible.map((d) => (
            <DeliverableCard key={d.sponsorship_id} d={d} />
          ))}
        </ul>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  count,
  variant,
  children,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  variant: "warning" | "success" | "muted";
  children: React.ReactNode;
}) {
  const colors = active
    ? variant === "warning"
      ? "bg-amber-100 text-amber-900 border-amber-300"
      : variant === "success"
        ? "bg-emerald-100 text-emerald-900 border-emerald-300"
        : "bg-muted text-foreground border-border"
    : "border-border/60 text-muted-foreground hover:bg-muted";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded border px-2 py-0.5 text-xs transition-colors ${colors}`}
    >
      {children} <span className="tabular-nums">{count}</span>
    </button>
  );
}

function DeliverableCard({ d }: { d: DeliverableItem }) {
  return (
    <li className="rounded-md border border-border bg-muted/20 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="line-clamp-1 font-medium">
            {d.url ? (
              <a
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                {d.title ?? `Video #${d.video_id}`}
              </a>
            ) : (
              d.title ?? `Video #${d.video_id}`
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            发布 {fmtDate(d.published_at)} · sponsorship #{d.sponsorship_id}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge variant={decisionVariant(d.review_status)}>{d.review_status}</Badge>
          <span className="text-[10px] text-muted-foreground">{d.confidence}</span>
        </div>
      </div>
      <ArchiveRow d={d} />
      <MilestoneTable videoID={d.video_id} />
    </li>
  );
}

// v2.5 — Drive archive status + manual trigger. Only meaningful for
// is_our_campaign rows (which is what this list filters to anyway via
// the campaign deliverables endpoint). 3 states:
//   - archived (drive_url set) → green badge + open-in-Drive link
//   - failed (archive_error set, no drive_url) → red badge + tooltip + retry
//   - pending (neither) → muted badge + "归档" button
function ArchiveRow({ d }: { d: DeliverableItem }) {
  const qc = useQueryClient();
  const archiveMut = useMutation({
    mutationFn: () => endpoints.archiveVideo(d.video_id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["campaign-deliverables"] });
      if (r.archived && r.drive_url) {
        // Quiet success — the badge will re-render. No alert clutter.
      } else if (r.error) {
        alert(`归档失败:${r.error}`);
      }
    },
    onError: (e: unknown) => {
      alert(`归档失败:${(e as Error).message}`);
    },
  });

  if (d.archive_drive_url) {
    return (
      <div className="mt-2 flex items-center gap-2 text-xs">
        <Badge variant="success">已归档</Badge>
        <a
          href={d.archive_drive_url}
          target="_blank"
          rel="noreferrer"
          className="text-muted-foreground hover:underline"
        >
          在 Drive 打开 ↗
        </a>
      </div>
    );
  }

  const failed = !!d.archive_error;
  return (
    <div className="mt-2 flex items-center gap-2 text-xs">
      {failed ? (
        <Badge variant="danger" title={d.archive_error}>
          归档失败
        </Badge>
      ) : (
        <Badge variant="outline">未归档</Badge>
      )}
      <button
        type="button"
        onClick={() => archiveMut.mutate()}
        disabled={archiveMut.isPending}
        className="rounded border border-border px-2 py-0.5 text-[10px] hover:bg-muted disabled:opacity-50"
        title="立即下载并上传到 Drive (10-30 秒)"
      >
        {archiveMut.isPending ? "归档中…" : failed ? "↻ 重试" : "↑ 归档"}
      </button>
    </div>
  );
}

function MilestoneTable({ videoID }: { videoID: number }) {
  const q = useQuery({
    queryKey: ["video-milestones", videoID],
    queryFn: () => endpoints.videoMilestones(videoID),
    staleTime: 60_000,
  });

  if (q.isLoading) return <p className="mt-2 text-[10px] text-muted-foreground">加载里程碑…</p>;

  // v1.4.4: backend always returns exactly the requested milestones (3 rows
  // for default 24/72/168), with empty captured_at when no snapshot is
  // within ±25% tolerance of that milestone time. Render all rows so the
  // operator sees "待采样" for milestones not yet hit, instead of just the
  // ones with data.
  const milestones = (q.data ?? []).slice().sort((a, b) => a.offset_hours - b.offset_hours);
  if (milestones.length === 0) {
    return (
      <p className="mt-2 text-[10px] text-muted-foreground">
        暂无时间序列快照
      </p>
    );
  }

  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="py-1 pr-3 font-medium">时点</th>
            <th className="py-1 pr-3 text-right font-medium">观看</th>
            <th className="py-1 pr-3 text-right font-medium">点赞</th>
            <th className="py-1 pr-3 text-right font-medium">评论</th>
            <th className="py-1 font-medium">采样时间</th>
          </tr>
        </thead>
        <tbody>
          {milestones.map((m) => {
            // Empty captured_at = no snapshot qualifies for this milestone yet
            // (either no snapshots collected or none within ±25% tolerance).
            const pending = !m.captured_at;
            return (
              <tr key={m.offset_hours} className="border-t border-border/60">
                <td className="py-1 pr-3">{labelForOffset(m.offset_hours)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {pending ? <span className="text-muted-foreground">—</span> : fmtNumber(m.view_count ?? null)}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {pending ? <span className="text-muted-foreground">—</span> : fmtNumber(m.like_count ?? null)}
                </td>
                <td className="py-1 pr-3 text-right tabular-nums">
                  {pending ? <span className="text-muted-foreground">—</span> : fmtNumber(m.comment_count ?? null)}
                </td>
                <td className="py-1 text-[10px] text-muted-foreground">
                  {pending ? "待采样" : fmtTimestamp(m.captured_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function labelForOffset(hours: number): string {
  if (hours < 24) return `+${hours}H`;
  const days = Math.round(hours / 24);
  return `+${days}D`;
}

export function RecordDeliverableModal({
  campaignID,
  cc,
  onClose,
  onSaved,
}: {
  campaignID: number;
  cc: CampaignChannelResponse;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    video_url: "",
    title: "",
    published_date: today,
    view_count: "",
    like_count: "",
    comment_count: "",
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: DeliverableRequest) =>
      endpoints.recordDeliverable(campaignID, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-deliverables", campaignID] });
      qc.invalidateQueries({ queryKey: ["campaign", campaignID] });
      qc.invalidateQueries({ queryKey: ["sponsorships"] });
      onSaved();
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.video_url.trim()) return setError("内容 URL 必填");
    const iso = new Date(`${form.published_date}T00:00:00Z`).toISOString();
    mutation.mutate({
      channel_id: cc.channel_id,
      video_url: form.video_url.trim(),
      title: form.title.trim() || undefined,
      published_at: iso,
      view_count: form.view_count ? Number(form.view_count) : undefined,
      like_count: form.like_count ? Number(form.like_count) : undefined,
      comment_count: form.comment_count ? Number(form.comment_count) : undefined,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        className="w-full max-w-xl rounded-lg border border-border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 className="mb-1 text-lg font-semibold">记录交付内容</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          频道 <strong>{cc.channel_name ?? cc.channel_handle ?? `#${cc.channel_id}`}</strong>
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="内容 URL *" full>
            <Input
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder={
                cc.channel_platform === "twitter"
                  ? "https://x.com/handle/status/..."
                  : cc.channel_platform === "tiktok"
                  ? "https://www.tiktok.com/@handle/video/..."
                  : "https://www.youtube.com/watch?v=..."
              }
              autoFocus
            />
          </Lbl>
          <Lbl label="标题（可选）" full>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Lbl>
          <Lbl label="发布日期 *">
            <Input
              type="date"
              value={form.published_date}
              onChange={(e) => setForm({ ...form, published_date: e.target.value })}
            />
          </Lbl>
          <Lbl label="观看数（可选）">
            <Input
              type="number"
              value={form.view_count}
              onChange={(e) => setForm({ ...form, view_count: e.target.value })}
            />
          </Lbl>
          <Lbl label="点赞数（可选）">
            <Input
              type="number"
              value={form.like_count}
              onChange={(e) => setForm({ ...form, like_count: e.target.value })}
            />
          </Lbl>
          <Lbl label="评论数（可选）">
            <Input
              type="number"
              value={form.comment_count}
              onChange={(e) => setForm({ ...form, comment_count: e.target.value })}
            />
          </Lbl>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </div>
  );
}

// EditCampaignChannelModal — edit a single KOL deal. v1.4.1 — deal_amount
// is now decrypted server-side (cc.deal_amount), so we CAN pre-fill it.
// Saving with a new value re-encrypts and overwrites; saving with empty
// clears the amount.
function EditCampaignChannelModal({
  cc,
  campaignID,
  onClose,
  onSaved,
}: {
  cc: CampaignChannelResponse;
  campaignID: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CampaignChannelDraft>({
    channel_id: cc.channel_id,
    referral_code: cc.referral_code ?? "",
    kol_uid: cc.kol_uid ?? "",
    agreed_deliverables: cc.agreed_deliverables ?? "",
    payment_currency: cc.payment_currency ?? "",
    notes: cc.notes ?? "",
    deal_amount: cc.deal_amount ?? undefined,
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CampaignChannelDraft) =>
      endpoints.updateCampaignChannel(campaignID, cc.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign", campaignID] });
      onSaved();
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      ...form,
      referral_code: form.referral_code || undefined,
      kol_uid: form.kol_uid || undefined,
      agreed_deliverables: form.agreed_deliverables || undefined,
      payment_currency: form.payment_currency || undefined,
      notes: form.notes || undefined,
      deal_amount: form.deal_amount ? Number(form.deal_amount) : undefined,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        className="w-full max-w-xl rounded-lg border border-border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 className="mb-1 text-lg font-semibold">编辑 KOL 合约</h2>
        <p className="mb-3 text-xs text-muted-foreground">
          频道：{cc.channel_name ?? cc.channel_handle ?? `#${cc.channel_id}`}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Lbl label="返佣码">
            <Input
              value={form.referral_code ?? ""}
              onChange={(e) => setForm({ ...form, referral_code: e.target.value })}
              autoFocus
            />
          </Lbl>
          <Lbl label="KOL 在 Bitbase 的 UID">
            <Input
              value={form.kol_uid ?? ""}
              onChange={(e) => setForm({ ...form, kol_uid: e.target.value })}
              placeholder="eg. 12345678"
            />
          </Lbl>
          <Lbl label="币种">
            <Input
              value={form.payment_currency ?? ""}
              onChange={(e) => setForm({ ...form, payment_currency: e.target.value })}
              placeholder="USD / USDT / CNY"
            />
          </Lbl>
          <Lbl label="金额（明文）">
            <Input
              type="number"
              step="0.01"
              value={form.deal_amount ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  deal_amount: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              placeholder="服务端 AES-GCM 加密存储"
            />
          </Lbl>
          <Lbl label="约定交付物" full>
            <Input
              value={form.agreed_deliverables ?? ""}
              onChange={(e) => setForm({ ...form, agreed_deliverables: e.target.value })}
              placeholder="eg. 1 主内容 + 2 短内容"
            />
          </Lbl>
          <Lbl label="备注" full>
            <textarea
              className="h-16 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={form.notes ?? ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Lbl>
        </div>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </div>
  );
}
